'use client'

import { useCallback, JSX } from 'react'

import { useForm, useWatch } from 'react-hook-form'
import { useHotkeys } from 'react-hotkeys-hook'

import { WorkspaceAutoEarnToggle } from '~/app/settings/team/general/WorkspaceAutoEarnToggle'
import { withAuthentication } from '~/components/auth/withAuthentication'
import { CommandKeyDecoration } from '~/components/command-menu/components/ShortcutDecoration'
import {
  Button,
  Group,
  PageLayout,
  TextInput,
  Toggleable,
} from '~/components/component-library'
import { PhotoEdit } from '~/components/settings/PhotoEdit'
import { SettingsDivider } from '~/components/settings/SettingsDivider'
import {
  SettingsPageContainer,
  settingsVisibility,
} from '~/components/settings/utils'
import {
  useActiveOrg,
  useActiveOrgMember,
  useActiveOrgUpdates,
  useRequiredActiveOrg,
} from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { useShowArchivedAccounts } from '~/hooks/useShowArchivedAccounts'
import { validateOrgName } from '~/utils/org'

const TeamGeneralPageInner = () => {
  const orgMember = useActiveOrgMember()

  return (
    <SettingsPageContainer
      requireOwner={settingsVisibility.general === 'owners'}
    >
      <PageLayout
        pageType="standard"
        title="General"
        description="Manage your workspace’s name, logo, and general settings."
      >
        <OrgNameEdit />
        <SettingsDivider />
        <OrgLogoEdit />
        {orgMember?.role === 'OWNER' && (
          <>
            <SettingsDivider />
            <WorkspaceAutoEarnToggle />
            <SettingsDivider />
            <ShowArchivedToggle />
            <SettingsDivider />
            <RequireMemoToggle />
          </>
        )}
      </PageLayout>
    </SettingsPageContainer>
  )
}

type EditOrgNameFormValues = {
  name: string
}

const OrgNameEdit = (): JSX.Element => {
  const { org: authOrg } = useActiveOrg()
  if (!authOrg) throw new Error('Org required')

  const { serverPutRequest, isProcessing } = useServerRequests()
  const orgMember = useActiveOrgMember()
  const { updateName } = useActiveOrgUpdates()

  const {
    control,
    reset,
    handleSubmit,
    formState: { defaultValues, errors },
  } = useForm<EditOrgNameFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: authOrg.name,
    },
  })
  const name = useWatch({ control, name: 'name' })

  const onSubmit = useCallback(
    async (data: EditOrgNameFormValues) => {
      const result = await serverPutRequest({
        path: '/api/org/edit/name',
        params: {
          orgId: authOrg.id,
          name: data.name,
        },
        processingMessage: 'Updating name...',
        successMessage: 'Name updated',
        errorMessage: 'Failed to update name',
      })
      if (result) {
        updateName(data.name)
        reset({
          name: data.name,
        })
      }
    },
    [authOrg.id, reset, serverPutRequest, updateName],
  )

  return (
    <section className="space-y-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextInput
          label="Workspace name"
          description="This is your workspace's visible name within the app"
          control={control}
          inputName={'name'}
          placeholder={'Splits Labs'}
          validationFunc={validateOrgName}
          autoFocus={false}
          disabled={authOrg.isPersonal || orgMember?.role !== 'OWNER'}
          disabledReason={
            authOrg.isPersonal
              ? 'Unable to change personal workspace name'
              : 'Only workspace owners can change the workspace name'
          }
        />
        <div className="flex justify-end space-x-4 pt-4">
          {authOrg.isPersonal ? (
            <p className="text-sm text-secondary">
              Unable to change personal workspace name
            </p>
          ) : (
            <Button
              isDisabled={
                authOrg.isPersonal ||
                name === defaultValues?.name ||
                !!errors.name
              }
              onClick={() => handleSubmit(onSubmit)}
              type={'submit'}
              eventName={'clickedEditTeamName'}
              eventProperties={{
                orgId: authOrg.id,
                name,
              }}
              isLoading={isProcessing}
            >
              Save
            </Button>
          )}
        </div>
      </form>
    </section>
  )
}

const OrgLogoEdit = (): JSX.Element => {
  const { org: authOrg } = useActiveOrg()
  if (!authOrg) throw new Error('Org required')

  const { serverPostRequest } = useServerRequests()
  const orgMember = useActiveOrgMember()
  const isDisabled = !orgMember || orgMember.role !== 'OWNER'
  const { updateLogoUrl } = useActiveOrgUpdates()

  const processFile = useCallback(
    async (file: File) => {
      const formData = new FormData()
      formData.append('logo', file)
      formData.append('orgId', authOrg.id)

      const result = await serverPostRequest<{
        logoUrl: string
      }>({
        path: '/api/org/edit/logo/upload',
        params: formData,
        processingMessage: 'Updating logo...',
        successMessage: 'Logo updated',
        errorMessage: 'Failed to update logo',
      })
      if (result) {
        updateLogoUrl(result.data.logoUrl)
      }
    },
    [authOrg.id, serverPostRequest, updateLogoUrl],
  )

  return (
    <Group
      label="Workspace logo"
      description="Click on the avatar to upload a custom image"
    >
      <PhotoEdit
        photoUrl={authOrg.logoUrl}
        photoAlt={'Logo'}
        isDisabled={isDisabled}
        processFile={processFile}
        shape="rounded"
      />
    </Group>
  )
}

const ShowArchivedToggle = (): JSX.Element => {
  const [showArchived, setShowArchived] = useShowArchivedAccounts()

  useHotkeys('meta+shift+period', () => setShowArchived(!showArchived))

  return (
    <Toggleable
      label="Show archived accounts"
      description={
        <span className="mt-2 flex items-center gap-1">
          Keyboard shortcut: <CommandKeyDecoration shortcut="meta+shift+." />
        </span>
      }
      isChecked={showArchived}
      onCheckedChange={setShowArchived}
    />
  )
}

const RequireMemoToggle = (): JSX.Element => {
  const org = useRequiredActiveOrg()
  const { serverPutRequest, isProcessing } = useServerRequests()
  const { updateRequireMemo } = useActiveOrgUpdates()

  const onToggle = async (requireMemo: boolean) => {
    const result = await serverPutRequest({
      path: '/api/org/edit/require-memo',
      params: { orgId: org.id, requireMemo },
      processingMessage: 'Updating setting...',
      successMessage: requireMemo
        ? 'Memos are now required'
        : 'Memos are no longer required',
      errorMessage: 'Failed to update setting',
    })
    if (result) {
      updateRequireMemo(requireMemo)
    }
  }

  return (
    <Toggleable
      label="Require memos"
      description="Require a memo on all transactions, automations, invoices, and schedules"
      isChecked={org.requireMemo}
      onCheckedChange={(requireMemo) => void onToggle(requireMemo)}
      isDisabled={isProcessing}
    />
  )
}
export const TeamGeneralPage = withAuthentication(TeamGeneralPageInner)
